import { useLocation } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

const LAST_SEEN_INVOICE_KEY = "invoice-pdf-last-seen-invoice";
const SYNC_STATE_KEY = "invoice-pdf-sync-state";
const SYNC_REQUEST_KEY = "invoice-pdf-sync-request";

type ViewerState = {
  coordinates?: any;
  pdfBlobData?: string | null;
};

type StatePayload = {
  type: "STATE";
  ts: number;
  invoiceId: string;
  state: ViewerState;
};

type RequestStatePayload = {
  type: "REQUEST_STATE";
  ts: number;
  invoiceId?: string;
};

/* ---------------------------
   Reusable helpers
--------------------------- */
function dispatchState(invoiceId: string, state: ViewerState) {
  try {
    const payload: StatePayload = {
      type: "STATE",
      invoiceId,
      state,
      ts: Date.now(),
    };
    localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(payload));
  } catch {}
}

function dispatchRequestState(invoiceId?: string) {
  try {
    const payload: RequestStatePayload = {
      type: "REQUEST_STATE",
      ts: Date.now(),
      invoiceId,
    };
    localStorage.setItem(SYNC_REQUEST_KEY, JSON.stringify(payload));
  } catch {}
}

function readStatePayload(): StatePayload | null {
  try {
    const raw = localStorage.getItem(SYNC_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StatePayload;
  } catch {
    return null;
  }
}

function readRequestPayload(): RequestStatePayload | null {
  try {
    const raw = localStorage.getItem(SYNC_REQUEST_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as RequestStatePayload;
  } catch {
    return null;
  }
}

function markLastSeenInvoice(invoiceId: string) {
  try {
    localStorage.setItem(LAST_SEEN_INVOICE_KEY, JSON.stringify({ invoiceId }));
  } catch {}
}

function readLastSeenInvoiceId(): string | undefined {
  try {
    const raw = localStorage.getItem(LAST_SEEN_INVOICE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as { invoiceId?: string };
    return parsed.invoiceId;
  } catch {
    return undefined;
  }
}

/* =========================
   Inline hook
   ========================= */
export function useInvoicePdfSyncSession(
  invoiceId: string,
  isFullscreen?: boolean,
) {
  const location = useLocation();

  const latestStateRef = useRef<ViewerState>({
    pdfBlobData: undefined,
    coordinates: undefined,
  });

  const sendState = useCallback(
    (state: ViewerState) => {
      if (isFullscreen) {
        return;
      }
      dispatchState(invoiceId, state);
    },
    [invoiceId, isFullscreen],
  );

  // Caller pushes state changes
  const publishState = useCallback(
    (state: ViewerState) => {
      if (isFullscreen) {
        return;
      }

      latestStateRef.current = state;
      sendState(state);
    },
    [sendState, isFullscreen],
  );

  // Listen for state requests from other tabs
  useEffect(() => {
    if (isFullscreen) {
      return;
    }

    const handleStorageEvent = (event: StorageEvent) => {
      // Only respond to request key changes
      if (event.key !== SYNC_REQUEST_KEY) {
        return;
      }

      const request = readRequestPayload();
      if (!request || request.type !== "REQUEST_STATE") {
        return;
      }

      if (request.invoiceId != null) {
        if (request.invoiceId === invoiceId) {
          sendState(latestStateRef.current);
        }
        return;
      }

      // Generic request: only visible tab responds to avoid conflicts
      if (document.visibilityState === "visible") {
        sendState(latestStateRef.current);
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [invoiceId, sendState, isFullscreen]);

  // Mark last-seen invoice on focus/visibility + republish latest
  useEffect(() => {
    if (isFullscreen) {
      return;
    }

    const onFocus = () => {
      markLastSeenInvoice(invoiceId);
      sendState(latestStateRef.current);
    };

    const clear = (reason: string) => {
      console.log("session-sync: clear", invoiceId, reason);
      sendState({
        pdfBlobData: null,
        coordinates: undefined,
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        onFocus();
      }
    };

    if (document.visibilityState === "visible") {
      markLastSeenInvoice(invoiceId);
    }

    const handlePageHide = () => clear("pagehide");
    const handleBeforeUnload = () => clear("beforeunload");

    window.addEventListener("focus", onFocus);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [invoiceId, sendState, isFullscreen]);

  useEffect(() => {
    return () => {
      if (!isFullscreen) {
        sendState({
          pdfBlobData: null,
          coordinates: undefined,
        });
      }
    };
  }, [location.pathname, invoiceId, sendState, isFullscreen]);

  return { invoiceId, publishState };
}

/* =========================
   Fullscreen hook
   ========================= */
export function useInvoicePdfFullscreenSync() {
  const [payload, setPayload] = useState<{
    invoiceId: string | undefined;
    viewState: ViewerState;
  }>({
    invoiceId: undefined,
    viewState: { pdfBlobData: undefined, coordinates: undefined },
  });

  const lastTsRef = useRef(0);
  const currentInvoiceIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let gotState = false;

    const processStatePayload = (msg: StatePayload) => {
      // Ignore older/out-of-order messages
      if (msg.ts < lastTsRef.current) {
        return;
      }
      lastTsRef.current = msg.ts;

      const isEmpty =
        msg.state && !msg.state.pdfBlobData && !msg.state.coordinates;

      if (!isEmpty) {
        gotState = true;
        currentInvoiceIdRef.current = msg.invoiceId;

        setPayload({
          viewState: msg.state,
          invoiceId: msg.invoiceId,
        });
        return;
      }

      // Empty only applies if it's clearing what we're currently showing.
      // This prevents tab1's clear from wiping tab2 after a switch.
      if (currentInvoiceIdRef.current === msg.invoiceId) {
        setPayload({
          viewState: msg.state,
          invoiceId: msg.invoiceId,
        });
      }
    };

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key !== SYNC_STATE_KEY) {
        return;
      }

      const msg = readStatePayload();
      if (!msg || msg.type !== "STATE") {
        return;
      }

      processStatePayload(msg);
    };

    window.addEventListener("storage", handleStorageEvent);

    const existingState = readStatePayload();
    if (existingState && existingState.type === "STATE") {
      processStatePayload(existingState);
      gotState = true;
    }

    const lastSeenInvoiceId = readLastSeenInvoiceId();
    dispatchRequestState(lastSeenInvoiceId);

    const fallbackTimer = window.setTimeout(() => {
      if (!gotState) {
        dispatchRequestState();
      }
    }, 500);

    return () => {
      clearTimeout(fallbackTimer);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);

  return payload;
}
